import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useEscapeLayer } from '../ui/Dialog';
import type { statusScopes, StatusScope } from './groups';
import { MANUAL_STATUSES, RENTAL_STATUSES, type RentalStatus } from './types';

/**
 * The status of a rental as a small button (owner 2026-10-23 15:00: „w statusie muszę móc ręcznie kliknąć w planowane”):
 * it opens the four statuses and, under them, for whom the change is (`scopes`, a radio group: „Tylko ta pozycja”,
 * „Do zaznaczonych pozycji (N)”, „Dla wszystkich pozycji od <dostawca>”), set to `scopes.default` each time it opens.
 * A choice saves at once — no „czy na pewno”. Escape, a click outside or Tab closes it.
 */
export function StatusChooser({ status, scopes, busy, onPick }: { status: RentalStatus; scopes: ReturnType<typeof statusScopes>; busy: boolean; onPick: (status: RentalStatus, scope: StatusScope) => void }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<StatusScope>(scopes.default);
  const name = useId();
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // fixed under the button, right edges aligned (the status is the last column: a list opening to the right would
  // leave the card); upwards when there is little room below; a scroll closes it
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    if (!open || !button.current) return setStyle(undefined);
    const r = button.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 260 && r.top > window.innerHeight - r.bottom;
    setStyle({ position: 'fixed', right: Math.max(8, window.innerWidth - r.right), left: 'auto', ...(up ? { bottom: window.innerHeight - r.top + 4, top: 'auto' } : { top: r.bottom + 4 }) });
    const close = (e: Event) => {
      if (!(e.target instanceof Node && root.current?.contains(e.target))) setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]')?.focus();
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);
  useEscapeLayer(() => {
    setOpen(false);
    button.current?.focus();
  }, open);
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitemradio"], input')];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Tab') setOpen(false);
  };
  return (
    <div className="row-menu text rental-status" ref={root}>
      <button
        ref={button}
        type="button"
        className={`rental-status-btn status-${status}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={`Status: ${RENTAL_STATUSES[status]} — zmień`}
        disabled={busy}
        onClick={() => {
          if (!open) setScope(scopes.default);
          setOpen((v) => !v);
        }}
      >
        {RENTAL_STATUSES[status]}
      </button>
      {open && (
        <div className="row-menu-pop rental-status-pop" role="menu" id={id} aria-label="Status dopożyczenia" onKeyDown={onKey} style={style}>
          {MANUAL_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={s === status}
              onClick={() => {
                setOpen(false);
                button.current?.focus();
                if (s !== status || scope !== 'one') onPick(s, scope);
              }}
            >
              {RENTAL_STATUSES[s]}
            </button>
          ))}
          {scopes.options.length > 1 && (
            <div className="rental-status-scope" role="radiogroup" aria-label="Zmień dla">
              {scopes.options.map((o) => (
                <label key={o.scope}>
                  <input type="radio" name={name} value={o.scope} checked={scope === o.scope} onChange={() => setScope(o.scope)} />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
